import {
	type BrandMentionFocal,
	buildBrandMentionMatcher,
	mentionMatchesAnyTerm,
	resolveMention,
} from "./brand-mention-match";
import { isOwnDomainCitation } from "./domain";
import { cleanAiOverviewMarkdownWithLists } from "./markdown-renderer";

interface DiscoveryQueryExecution {
	id: string;
	platform: string;
	model: string;
	browsing: boolean;
	answerSummary: string;
	ourBrandInSummary: boolean;
	ourBrandRankInSummary?: number;
	totalCitations: number;
	ourDomainCitations: number;
	completedAt?: string;
	error?: string;
	citations: Array<{
		id: string;
		url: string;
		title?: string;
		description?: string;
		isOurDomain: boolean;
		brandAttributed?: string;
	}>;
	brandMentions: Array<{
		id: string;
		brandName: string;
		isOurBrand: boolean;
		isCompetitor: boolean;
		inSummary: boolean;
		summaryRank?: number;
		context?: string;
	}>;
	// Google AI Overview specific data
	googleAiOverview?: {
		check_url: string;
		aiOverview?: {
			summary: string;
			sections: Array<{
				title?: string;
				content: string;
			}>;
			brandMentions: Array<{
				name: string;
				description: string;
				domain: string;
				url: string;
			}>;
		};
		organicResults: Array<{
			rank: number;
			title: string;
			domain: string;
			websiteName: string;
			url: string;
			description: string;
		}>;
	};
}

// Helper function to transform database discovery execution to frontend format
export function transformDiscoveryExecution(
	dbExecution: any,
): DiscoveryQueryExecution {
	const execution: DiscoveryQueryExecution = {
		id: dbExecution.id,
		platform: dbExecution.platform,
		model: dbExecution.model,
		browsing: dbExecution.browsing,
		answerSummary: dbExecution.answerSummary,
		ourBrandInSummary: dbExecution.ourBrandInSummary,
		ourBrandRankInSummary: dbExecution.ourBrandRankInSummary,
		totalCitations: dbExecution.totalCitations,
		ourDomainCitations: dbExecution.ourDomainCitations,
		completedAt: dbExecution.completedAt,
		error: dbExecution.error,
		citations: dbExecution.citations || [],
		brandMentions: dbExecution.brandMentions || [],
	};

	// Handle Google AI Overview data
	if (
		dbExecution.platform === "google_ai_overview" &&
		dbExecution.rawResponse
	) {
		execution.googleAiOverview = dbExecution.rawResponse;
	}

	return execution;
}

/**
 * Transform raw Google AI Overview data from DataForSEO API to frontend format
 */
export function transformRawGoogleAiOverview(rawData: any): {
	check_url: string;
	aiOverview?: {
		summary: string;
		/** Full markdown content for rendering and brand highlighting */
		markdown?: string;
		sections: Array<{
			title?: string;
			content: string;
		}>;
		/** Reference-based brand mentions (cited domains/sources) */
		brandMentions: Array<{
			name: string;
			description: string;
			domain: string;
			url: string;
		}>;
		/** Text-based brand mentions extracted from markdown (bold text, headers) */
		textBrandMentions?: Array<{
			name: string;
			context: string;
			position: number;
		}>;
	};
	organicResults: Array<{
		rank: number;
		title: string;
		domain: string;
		websiteName: string;
		url: string;
		description: string;
	}>;
} {
	// Handle case where rawData is already processed (GoogleAiOverviewResult)
	// Processed data will have aiOverview property, raw DataForSEO data will have items array
	if (rawData.aiOverview !== undefined) {
		return {
			check_url: rawData.checkUrl || rawData.check_url || "",
			aiOverview: rawData.aiOverview,
			organicResults: rawData.organicResults || [],
		};
	}

	const result = {
		check_url: rawData.check_url || "",
		organicResults: [] as Array<{
			rank: number;
			title: string;
			domain: string;
			websiteName: string;
			url: string;
			description: string;
		}>,
	};

	// Find AI Overview in the raw items
	const aiOverviewItem = rawData.items?.find(
		(item: any) => item.type === "ai_overview",
	);

	if (aiOverviewItem) {
		const sections: Array<{ title?: string; content: string }> = [];
		let summary = "";

		// Process AI Overview items
		if (aiOverviewItem.items) {
			for (const item of aiOverviewItem.items) {
				if (item.type === "ai_overview_element") {
					const cleanedContent = cleanMarkdownBasic(
						item.markdown || item.text || "",
					);

					if (!summary && !item.title) {
						// First item without title is the summary
						summary = cleanedContent;
					} else {
						// Other items are sections
						sections.push({
							title: item.title,
							content: cleanedContent,
						});
					}
				}
			}
		}

		// Extract brand mentions from references
		const brandMentions: Array<{
			name: string;
			description: string;
			domain: string;
			url: string;
		}> = [];

		const mentionsMap = new Map<string, any>();

		// Extract from main references
		if (aiOverviewItem.references) {
			for (const ref of aiOverviewItem.references) {
				if (ref.source && ref.domain && ref.url) {
					mentionsMap.set(ref.domain, {
						name: ref.source,
						description: ref.title || "",
						domain: ref.domain,
						url: ref.url,
					});
				}
			}
		}

		// Extract from individual ai_overview_element references
		if (aiOverviewItem.items) {
			for (const item of aiOverviewItem.items) {
				if (item.type === "ai_overview_element" && item.references) {
					for (const ref of item.references) {
						if (ref.source && ref.domain && ref.url) {
							mentionsMap.set(ref.domain, {
								name: ref.source,
								description: ref.title || "",
								domain: ref.domain,
								url: ref.url,
							});
						}
					}
				}
			}
		}

		brandMentions.push(...Array.from(mentionsMap.values()));

		(result as any).aiOverview = {
			summary,
			sections,
			brandMentions,
		};
	}

	// Extract organic results
	const organicItems =
		rawData.items?.filter((item: any) => item.type === "organic") || [];

	result.organicResults = organicItems
		.sort((a: any, b: any) => a.rank_group - b.rank_group)
		.map((item: any) => ({
			rank: item.rank_group,
			title: item.title,
			domain: item.domain,
			websiteName: item.website_name,
			url: item.url,
			description: item.description,
		}));

	return result;
}

/**
 * Basic markdown cleaning for frontend use
 * @deprecated Use cleanAiOverviewMarkdownWithLists from markdown-renderer.ts directly
 */
const cleanMarkdownBasic = cleanAiOverviewMarkdownWithLists;

interface DiscoveryExecution {
	id: string;
	propertyQuery: {
		id: string;
		query: string;
		queryType: string;
		category?: string;
		useCase?: string;
		budget?: string;
		segment?: string;
		comparisonFocus?: string;
	};
	platformExecutions: DiscoveryQueryExecution[];
}

// Helper functions for competitor detection and Share of Voice calculation

/**
 * Get all competitor names including aliases for brand mention detection.
 * Returns a flattened array of all names and aliases (lowercased).
 *
 * @param brandSnapshot - Legacy brand snapshot data (fallback)
 * @param propertyCompetitors - Array of competitor objects from property.competitors
 * @returns Array of competitor names and aliases (lowercased)
 */
export function getCompetitorNames(
	brandSnapshot?: any,
	propertyCompetitors?: any[],
): string[] {
	// First try to get competitors from property.competitors (the correct source)
	if (propertyCompetitors && propertyCompetitors.length > 0) {
		const names: string[] = [];

		for (const comp of propertyCompetitors) {
			// Add primary name (or extract from URL if no name)
			const primaryName =
				comp.name ||
				comp.url
					?.replace(/^https?:\/\//, "")
					.replace(/^www\./, "")
					.split("/")[0];
			if (primaryName) {
				names.push(primaryName.toLowerCase());
			}

			// Add all aliases
			if (comp.aliases && Array.isArray(comp.aliases)) {
				for (const alias of comp.aliases) {
					if (alias && typeof alias === "string") {
						names.push(alias.toLowerCase());
					}
				}
			}
		}

		return names.filter(Boolean);
	}

	// Fallback to brand snapshot data (legacy)
	if (!brandSnapshot?.data) return [];

	// Get competitors from the brand snapshot data
	const competitors = brandSnapshot.data.competitors || [];

	// Also check if competitors are stored directly on brandSnapshot
	const alternativeCompetitors = brandSnapshot.competitors || [];

	// Combine both sources and extract names
	const allCompetitors = [...competitors, ...alternativeCompetitors];
	const competitorNames = allCompetitors
		.map((comp: any) => comp.name || comp)
		.filter(Boolean)
		.map((name: string) => name.toLowerCase());

	return competitorNames;
}

/**
 * Our brand's identity for the "other side" of a competitor check: the same
 * `(brandSnapshot, propertyAliases)` pair `isOurBrand` takes.
 */
export interface OurBrandIdentity {
	brandSnapshot?: any;
	propertyAliases?: string[];
}

/**
 * Resolve a mention against our brand AND a flat competitor name list with the
 * canonical `resolveMention` (longest matching term wins, our brand on ties).
 */
function resolveAgainstBothSides(
	brandName: string,
	brandSnapshot: any,
	propertyAliases: string[] | undefined,
	competitorNames: string[],
) {
	return resolveMention(
		brandName,
		buildBrandMentionMatcher({
			focal: ourBrandMatchFocal(brandSnapshot, propertyAliases),
			competitors: competitorNames.map((name, i) => ({
				id: String(i),
				name,
			})),
		}),
	);
}

/**
 * Whether a raw mention string names any of the given competitor names /
 * aliases. Delegates to the canonical matcher (`./brand-mention-match`): both
 * sides are normalized (punctuation, `&`, apostrophes, markdown, legal
 * suffixes) and a name matches when it equals the mention or appears in it as
 * a whole-word run.
 *
 * Without `ourBrand` this is a ONE-SIDED check: it cannot know that a longer
 * brand name claims the mention (our brand "Kirkland & Ellis" vs a competitor
 * "Kirkland" → the mention "Kirkland & Ellis" is ours, yet this returns true).
 * Pass `ourBrand` whenever the caller has it to get the same attribution as
 * `resolveMention`.
 */
export function isCompetitorBrand(
	brandName: string,
	competitorNames: string[],
	ourBrand?: OurBrandIdentity,
): boolean {
	if (!ourBrand) return mentionMatchesAnyTerm(brandName, competitorNames);
	return (
		resolveAgainstBothSides(
			brandName,
			ourBrand.brandSnapshot,
			ourBrand.propertyAliases,
			competitorNames,
		)?.kind === "competitor"
	);
}

/**
 * The terms our brand is matched on: the snapshot's primary name plus every
 * property alias. Shared by `isOurBrand` and the SOV/grid matcher builders.
 */
export function ourBrandMatchFocal(
	brandSnapshot?: any,
	propertyAliases?: string[],
): BrandMentionFocal {
	const name = brandSnapshot?.data?.name;
	return {
		name: typeof name === "string" ? name : null,
		aliases: propertyAliases ?? [],
	};
}

/**
 * Check if a brand name matches our brand (including aliases), using the
 * canonical matcher (`./brand-mention-match`).
 *
 * Without `competitorNames` this is a ONE-SIDED check: with our brand
 * "Kirkland" it returns true for "Kirkland & Ellis", because it cannot know
 * that a competitor with a longer, more specific name claims that mention.
 * Pass `competitorNames` whenever the caller has them to get the same
 * attribution as `resolveMention` (longest match wins, ours on ties).
 *
 * @param brandName - The brand name to check
 * @param brandSnapshot - Brand snapshot row; the primary name is `data.name`
 * @param propertyAliases - Optional array of property aliases to check against
 * @param competitorNames - Optional competitor names/aliases (the other side)
 * @returns True if the mention belongs to our brand
 */
export function isOurBrand(
	brandName: string,
	brandSnapshot?: any,
	propertyAliases?: string[],
	competitorNames?: string[],
): boolean {
	if (competitorNames && competitorNames.length > 0) {
		return (
			resolveAgainstBothSides(
				brandName,
				brandSnapshot,
				propertyAliases,
				competitorNames,
			)?.kind === "self"
		);
	}
	const focal = ourBrandMatchFocal(brandSnapshot, propertyAliases);
	return mentionMatchesAnyTerm(brandName, [
		focal.name,
		...(focal.aliases ?? []),
	]);
}

/**
 * Returns true if our brand appears in this platform execution's brandMentions array.
 * Source of truth used by summary, per-query rows, per-platform dots, and badges.
 *
 * A "mention" means: the brand was named in the AI's response text. Citations and
 * organic search results don't count. The Google AI Overview job stores its organic
 * search results as DiscoveryBrandMention rows with inSummary: false so they can
 * power highlighting, but they are explicitly not AI mentions — this helper filters
 * them out by requiring inSummary === true.
 *
 * Recomputes live from brandMentions[] each call; does not trust stored
 * mention.isOurBrand or execution.ourBrandInSummary (those may be stale relative
 * to current property aliases).
 *
 * Pass `competitorNames` when available so a mention a longer competitor name
 * claims ("Kirkland & Ellis" vs our "Kirkland") is not counted as ours; see
 * `isOurBrand`.
 */
export function isBrandMentionedInExecution(
	execution: {
		brandMentions: Array<{
			brandName: string;
			isOurBrand?: boolean;
			inSummary?: boolean;
		}>;
		error?: string | null;
		completedAt?: string | Date | null;
	},
	brandSnapshot?: any,
	propertyAliases?: string[],
	competitorNames?: string[],
): boolean {
	if (execution.error || !execution.completedAt) return false;
	return execution.brandMentions.some(
		(m) =>
			m.inSummary === true &&
			isOurBrand(
				m.brandName,
				brandSnapshot,
				propertyAliases,
				competitorNames,
			),
	);
}

/**
 * Returns true if any competitor appears in this platform execution's brandMentions array.
 * Symmetric to isBrandMentionedInExecution — same inSummary filter, same live-match logic.
 * Citations and organic-result rows are not considered.
 */
export function isCompetitorMentionedInExecution(
	execution: {
		brandMentions: Array<{
			brandName: string;
			isCompetitor?: boolean;
			inSummary?: boolean;
		}>;
		error?: string | null;
		completedAt?: string | Date | null;
	},
	competitorNames: string[],
	ourBrand?: OurBrandIdentity,
): boolean {
	if (execution.error || !execution.completedAt) return false;
	return execution.brandMentions.some(
		(m) =>
			m.inSummary === true &&
			isCompetitorBrand(m.brandName, competitorNames, ourBrand),
	);
}

/**
 * Calculate share of voice for a single execution.
 *
 * @param execution - The discovery query execution
 * @param competitorNames - Array of competitor names and aliases
 * @param brandSnapshot - Brand snapshot with primary name
 * @param propertyAliases - Optional array of property aliases
 */
export function calculateShareOfVoice(
	execution: DiscoveryQueryExecution,
	competitorNames: string[],
	brandSnapshot?: any,
	propertyAliases?: string[],
): {
	shareOfVoice: number;
	ourMentions: number;
	competitorMentions: number;
	totalMentions: number;
} {
	let ourMentions = 0;
	let competitorMentions = 0;

	// Only count mentions stored as part of the AI response (inSummary === true).
	// Excludes Google AI Overview's organic-result rows (inSummary === false).
	execution.brandMentions.forEach((mention: any) => {
		if (mention.inSummary !== true) return;
		// One mention, one owner: longest matching name wins, ours on ties.
		const owner = resolveAgainstBothSides(
			mention.brandName,
			brandSnapshot,
			propertyAliases,
			competitorNames,
		);
		if (owner?.kind === "self") {
			ourMentions++;
		} else if (owner?.kind === "competitor") {
			competitorMentions++;
		}
	});

	const totalMentions = ourMentions + competitorMentions;
	const shareOfVoice =
		totalMentions > 0 ? (ourMentions / totalMentions) * 100 : 0;

	return {
		shareOfVoice: Math.round(shareOfVoice),
		ourMentions,
		competitorMentions,
		totalMentions,
	};
}

/**
 * Calculate query-level share of voice based on platform appearances.
 * SOV = share of platforms where the brand was named in the AI response text.
 * Citations and organic search results do not count — they are separate signals
 * tracked by their own metrics. See isBrandMentionedInExecution for the contract.
 *
 * @param discoveryExecution - The discovery execution with platform results
 * @param competitorNames - Array of competitor names and aliases
 * @param brandSnapshot - Brand snapshot with primary name
 * @param propertyAliases - Optional array of property aliases
 */
export function calculateQueryShareOfVoice(
	discoveryExecution: DiscoveryExecution,
	competitorNames: string[],
	brandSnapshot?: any,
	propertyAliases?: string[],
): {
	shareOfVoice: number;
	ourPlatformMentions: number;
	competitorPlatformMentions: number;
	totalPlatforms: number;
} {
	const completedPlatforms = discoveryExecution.platformExecutions.filter(
		(pe) => !pe.error && pe.completedAt,
	);
	const totalPlatforms = completedPlatforms.length;

	const ourPlatformMentions = completedPlatforms.filter((pe) =>
		isBrandMentionedInExecution(
			pe,
			brandSnapshot,
			propertyAliases,
			competitorNames,
		),
	).length;

	const competitorPlatformMentions = completedPlatforms.filter((pe) =>
		isCompetitorMentionedInExecution(pe, competitorNames, {
			brandSnapshot,
			propertyAliases,
		}),
	).length;

	// Share of voice = platforms where brand appears / total platforms tested
	const shareOfVoice =
		totalPlatforms > 0 ? (ourPlatformMentions / totalPlatforms) * 100 : 0;

	return {
		shareOfVoice: Math.round(shareOfVoice),
		ourPlatformMentions,
		competitorPlatformMentions,
		totalPlatforms,
	};
}

/**
 * Calculate summary statistics for discovery results.
 *
 * @param discoveryResults - Array of discovery executions
 * @param brandSnapshot - Brand snapshot with primary name
 * @param propertyCompetitors - Array of competitor objects from property.competitors
 * @param propertyAliases - Optional array of property aliases
 * @param brandDomain - The property's domain, or `null` for subjects that have
 *   none (PERSON / CATEGORY). Required — not optional — because omitting it used
 *   to mean "trust `citation.isOurDomain`", and for a domainless property that
 *   flag is `true` on every citation (the `anyUrl.includes("")` bug). Passing it
 *   explicitly forces every caller to answer "does this subject own a website?"
 *   rather than inheriting a silently wrong default.
 */
export function calculateSummaryStatistics(
	discoveryResults: DiscoveryExecution[],
	brandSnapshot: any,
	propertyCompetitors: any[] | undefined,
	propertyAliases: string[] | undefined,
	brandDomain: string | null,
) {
	const competitorNames = getCompetitorNames(
		brandSnapshot,
		propertyCompetitors,
	);

	// Defensive: discovery executions can arrive from either the report endpoint
	// (client-shaped) or the DB; tolerate missing nested arrays rather than crash.
	const platformExecsOf = (
		dr: DiscoveryExecution,
	): DiscoveryQueryExecution[] => dr.platformExecutions ?? [];

	// Calculate summary metrics including Share of Voice
	const totalExecutions = discoveryResults.reduce(
		(acc: number, dr: DiscoveryExecution) =>
			acc + platformExecsOf(dr).length,
		0,
	);
	const successfulExecutions = discoveryResults.reduce(
		(acc: number, dr: DiscoveryExecution) =>
			acc +
			platformExecsOf(dr).filter(
				(pe: DiscoveryQueryExecution) => !pe.error,
			).length,
		0,
	);

	// Count platforms where our brand appears in the response text (brandMentions array).
	// Citations are tracked separately (citationCount below) and do not count toward this stat.
	// Uses the shared isBrandMentionedInExecution helper so summary, per-query rows, and
	// per-platform indicators all answer this question the same way.
	const brandMentions = discoveryResults.reduce(
		(acc: number, dr: DiscoveryExecution) =>
			acc +
			platformExecsOf(dr).filter((pe: DiscoveryQueryExecution) =>
				isBrandMentionedInExecution(
					pe,
					brandSnapshot,
					propertyAliases,
					competitorNames,
				),
			).length,
		0,
	);

	// Count citations where our brand is mentioned (in citation sources, not just our domain)
	const citationCount = discoveryResults.reduce(
		(acc: number, dr: DiscoveryExecution) =>
			acc +
			platformExecsOf(dr).reduce(
				(pacc: number, pe: DiscoveryQueryExecution) => {
					if (pe.error) return pacc;
					// Count citations that mention our brand (either through domain or brand attribution)
					const ourBrandCitations = (pe.citations ?? []).filter(
						(citation: any) =>
							isOwnDomainCitation(
								citation.isOurDomain,
								brandDomain,
							) ||
							(citation.brandAttributed &&
								isOurBrand(
									citation.brandAttributed,
									brandSnapshot,
									propertyAliases,
								)),
					).length;
					return pacc + ourBrandCitations;
				},
				0,
			),
		0,
	);

	// Calculate overall Share of Voice based on platform appearances
	let totalOurPlatformMentions = 0;
	let totalCompetitorPlatformMentions = 0;
	let totalPlatformsTested = 0;

	discoveryResults.forEach((dr: DiscoveryExecution) => {
		const querySOV = calculateQueryShareOfVoice(
			dr,
			competitorNames,
			brandSnapshot,
			propertyAliases,
		);
		totalOurPlatformMentions += querySOV.ourPlatformMentions;
		totalCompetitorPlatformMentions += querySOV.competitorPlatformMentions;
		totalPlatformsTested += querySOV.totalPlatforms;
	});

	// Share of voice = our platform mentions / total platforms tested
	const overallShareOfVoice =
		totalPlatformsTested > 0
			? Math.round(
					(totalOurPlatformMentions / totalPlatformsTested) * 100,
				)
			: 0;

	return {
		totalQueries: discoveryResults.length,
		brandMentions,
		shareOfVoice: overallShareOfVoice,
		citations: citationCount,
		totalOurPlatformMentions,
		totalCompetitorPlatformMentions,
	};
}

/**
 * Highlight brand mentions in text (excluding citations).
 * Highlights our brand (and aliases) in blue and competitors (and their aliases) in orange.
 *
 * @param text - The text to highlight
 * @param competitorNames - Array of competitor names and aliases (from getCompetitorNames)
 * @param brandSnapshot - Brand snapshot containing the primary brand name
 * @param excludeCitations - Whether to skip highlighting text that looks like citations
 * @param propertyAliases - Optional array of property aliases to highlight as our brand
 */
export function highlightCompetitorMentions(
	text: string,
	competitorNames: string[],
	brandSnapshot?: any,
	excludeCitations = false,
	propertyAliases?: string[],
): string {
	if (!text) {
		return text;
	}

	// If excluding citations and this looks like a citation, don't highlight
	if (
		excludeCitations &&
		(text.includes("http") || text.includes("www.") || text.length < 100)
	) {
		return text;
	}

	let highlightedText = text;
	const highlightStyleOurBrand =
		"background-color: #bfdbfe; color: #1d4ed8; padding: 2px 4px; border-radius: 4px; font-weight: 500;"; // blue for our brand

	// Collect all our brand names (primary + aliases)
	const ourBrandNames: string[] = [];
	if (brandSnapshot?.data?.name) {
		ourBrandNames.push(brandSnapshot.data.name);
	}
	if (propertyAliases && propertyAliases.length > 0) {
		ourBrandNames.push(...propertyAliases.filter(Boolean));
	}

	// Highlight our brand names (sort by length to avoid partial matches)
	if (ourBrandNames.length > 0) {
		const sortedBrandNames = [...ourBrandNames].sort(
			(a, b) => b.length - a.length,
		);

		for (const brandName of sortedBrandNames) {
			if (!brandName) continue;
			const escapedBrand = brandName.replace(
				/[.*+?^${}()|[\]\\]/g,
				"\\$&",
			);
			const regex = new RegExp(`\\b(${escapedBrand})\\b`, "gi");
			highlightedText = highlightedText.replace(
				regex,
				`<span style="${highlightStyleOurBrand}">$1</span>`,
			);
		}
	}

	// Highlight competitors (sort by length to avoid partial matches)
	if (competitorNames.length > 0) {
		const sortedCompetitors = [...competitorNames].sort(
			(a, b) => b.length - a.length,
		);

		for (const competitorName of sortedCompetitors) {
			if (!competitorName) continue;

			// Create a case-insensitive regex that matches the competitor name
			const escapedBrand = competitorName.replace(
				/[.*+?^${}()|[\]\\]/g,
				"\\$&",
			);

			// For brands with special characters, use more flexible word boundaries
			let regex: RegExp;
			if (competitorName.match(/[+\-&!@#$%^*()]/)) {
				// For special characters, use lookahead/lookbehind or start/end boundaries
				regex = new RegExp(
					`(^|\\s|\\b)(${escapedBrand})(?=\\s|\\b|$)`,
					"gi",
				);
			} else {
				// For normal brands, use standard word boundaries
				regex = new RegExp(`\\b(${escapedBrand})\\b`, "gi");
			}
			const highlightStyle =
				"background-color: #fed7aa; color: #ea580c; padding: 2px 4px; border-radius: 4px; font-weight: 500;"; // orange for competitors

			// Adjust replacement based on regex pattern
			if (competitorName.match(/[+\-&!@#$%^*()]/)) {
				highlightedText = highlightedText.replace(
					regex,
					`$1<span style="${highlightStyle}">$2</span>`,
				);
			} else {
				highlightedText = highlightedText.replace(
					regex,
					`<span style="${highlightStyle}">$1</span>`,
				);
			}
		}
	}

	return highlightedText;
}
